import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react"

import type { PersonaMetadata } from "@/types/api/Persona"

import {
  getAvailableLanguages,
  getAvailableVoices,
  getMetadataForLanguageSelection,
  getMetadataForVoiceSelection,
} from "../utils/personaModalUtils"
import { isSilentMode, type PersonaFormData } from "../utils/personaValidation"

interface UsePersonaVoiceConfigurationOptions {
  metadata: PersonaFormData["metadata"]
  setFormData: Dispatch<SetStateAction<PersonaFormData>>
}

export const usePersonaVoiceConfiguration = ({
  metadata,
  setFormData,
}: UsePersonaVoiceConfigurationOptions) => {
  const typedMetadata = metadata as PersonaMetadata | undefined
  const currentLanguageCode = typedMetadata?.language_code
  const currentVoiceName = typedMetadata?.voice_name

  const isInSilentMode = useMemo(() => isSilentMode(metadata), [metadata])

  const availableVoices = useMemo(
    () => getAvailableVoices(currentLanguageCode),
    [currentLanguageCode],
  )

  const availableLanguages = useMemo(
    () => getAvailableLanguages(currentVoiceName),
    [currentVoiceName],
  )

  const handleVoiceSelection = useCallback(
    (selectedVoiceName?: string) => {
      setFormData((previous) => ({
        ...previous,
        metadata: getMetadataForVoiceSelection(
          previous.metadata as PersonaMetadata | undefined,
          selectedVoiceName,
        ),
      }))
    },
    [setFormData],
  )

  const handleLanguageSelection = useCallback(
    (selectedLanguageCode?: string) => {
      setFormData((previous) => ({
        ...previous,
        metadata: getMetadataForLanguageSelection(
          previous.metadata as PersonaMetadata | undefined,
          selectedLanguageCode,
        ),
      }))
    },
    [setFormData],
  )

  return {
    metadata: typedMetadata,
    isInSilentMode,
    availableVoices,
    availableLanguages,
    handleVoiceSelection,
    handleLanguageSelection,
  }
}
